/**
 * Unit 3.IMPL — DraggableRow rebuild (insurescope-zkolv.1.3.3).
 *
 * Shadcn `<TableRow>` wrapper that:
 *   - Surfaces `data-row-id` so `useLibraryDragAndDrop` can locate the row.
 *   - Wires `attachDraggable` (own rows) and `attachDropTarget` (own folders)
 *     when `dragAndDropEnabled` is true and a `dragItem` exists. A foreign
 *     shared row stays a plain `<TableRow>` with no DnD attachments.
 *   - Fades the row to 40% opacity when `isBeingDragged` is true (the parent
 *     derives this from its `draggedItemIds` Set so multi-select drags fade
 *     all selected rows together).
 *
 * Drag-preview rendering — LOAD-BEARING recipe, do NOT refactor:
 *   - The browser captures the drag image synchronously during the
 *     `dragstart` event. React 19's `createRoot.render` is async.
 *   - The preview is therefore mounted into a detached container with
 *     `flushSync` so the DOM is written before the browser snapshots it.
 *   - After the browser captures the image, the root is unmounted via
 *     `setTimeout(..., 0)` to avoid orphan React trees.
 *   - All three pieces (createRoot, flushSync, setTimeout-unmount) are
 *     load-bearing. Removing any one of them produces a blank preview.
 *
 * @see useLibraryDragAndDrop.tsx — owns the
 *   `pointerOutsideOfPreview({ x: "16px", y: "8px" })` offset that positions
 *   the preview relative to the cursor.
 */
import { useCallback } from "react";
import { flushSync } from "react-dom";
import { createRoot } from "react-dom/client";

import { TableRow } from "@/components/ui/table";
import { cn } from "@/lib/utils";

import type { ILibraryDragItem } from "../hooks/useLibraryDragAndDrop";

import { DragPreview } from "./DragPreview";

interface DraggableRowProps {
    rowId: string;
    /**
     * Pre-built drag item for THIS row, or null when the row's type is not
     * draggable. The caller resolves this via `getDragItem(rowId)` upstream
     * and threads the result through; this component does no resolution.
     */
    dragItem: ILibraryDragItem | null;
    /**
     * Whole-table flag — when false, this component falls back to a plain
     * `<TableRow>` with no DnD attachments. Covers the DnD-disabled paths
     * (select / move-modal) with the same component.
     */
    dragAndDropEnabled: boolean;
    attachDraggable: (
        element: HTMLElement,
        item: ILibraryDragItem,
        onGeneratePreview: (container: HTMLElement, count: number) => void
    ) => () => void;
    /** Folder rows only; caller is responsible for the `dragItem.type` check. */
    attachDropTarget: (
        element: HTMLElement,
        folderRowId: string,
        folderNumericId: number,
        folderName: string,
        isForeignShared: boolean
    ) => () => void;
    isBeingDragged: boolean;
    className?: string;
    onClick?: React.MouseEventHandler<HTMLTableRowElement>;
    /** shadcn `<TableRow>` surfaces this for the `data-[state=selected]` variant. */
    "data-state"?: "selected" | (string & {});
    /**
     * When true, the row is keyboard-activatable: `tabIndex={0}`, `role="button"`,
     * and Enter/Space synthesize a click on the row. Set by the parent based
     * on whether the row carries the `library-row--clickable` state class.
     * Without this, mouse-only users can interact but keyboard users can't.
     */
    interactive?: boolean;
    /** Accessible name for the row when `interactive` is true. */
    "aria-label"?: string;
    children: React.ReactNode;
}

export function DraggableRow({
    rowId,
    dragItem,
    dragAndDropEnabled,
    attachDraggable,
    attachDropTarget,
    isBeingDragged,
    className,
    onClick,
    interactive,
    children,
    ...rest
}: DraggableRowProps) {
    // Enter/Space → synthesize a click on the row, which routes through the
    // same `onClick` handler the mouse path uses. Only activate when the row
    // itself is focused: a descendant's keypress bubbles here through the React
    // tree (even from a portaled dialog input, e.g. the folder-rename field),
    // and `e.currentTarget.click()` would re-target the synthesized click to
    // the row — slipping past useRowClickHandler's descendant guard and
    // navigating mid-edit. The target check keeps descendant typing (Space,
    // Enter-to-submit) with its own element.
    const onKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLTableRowElement>) => {
            if (!interactive || !onClick) return;
            if (e.target !== e.currentTarget) return;
            if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                e.currentTarget.click();
            }
        },
        [interactive, onClick]
    );
    const onGeneratePreview = useCallback(
        (container: HTMLElement, count: number) => {
            if (!dragItem) return;
            // ILibraryDragItem.type is a strict subset of LibraryItemTypeKey
            // (no "knowledge_base"); DragPreview derives the KB variant from
            // the isKnowledgeBase flag. No cast needed.
            const root = createRoot(container);
            try {
                flushSync(() => {
                    root.render(
                        <DragPreview
                            name={dragItem.name}
                            type={dragItem.type}
                            count={count}
                            isKnowledgeBase={dragItem.isKnowledgeBase}
                        />
                    );
                });
            } finally {
                // setTimeout(0) is load-bearing (Decision D6) — synchronously
                // unmounting races pragmatic-dnd's snapshot. finally guarantees
                // the unmount even if flushSync throws.
                setTimeout(() => root.unmount(), 0);
            }
        },
        [dragItem]
    );

    // React 19 ref-callback cleanup form: returning a function from the ref
    // callback runs on unmount (or before the next attach when the callback
    // identity changes), so attach/detach pair up without a useEffect.
    const rowRef = useCallback(
        (el: HTMLTableRowElement | null) => {
            if (!el || !dragAndDropEnabled || !dragItem) return;
            // A row shared in by a group owner is read-only: it is neither
            // a drag source nor a drop target.
            if (dragItem.isForeignShared) return;
            const cleanups: Array<() => void> = [
                attachDraggable(el, dragItem, onGeneratePreview),
            ];
            if (dragItem.type === "folder" && !dragItem.isForeignShared) {
                cleanups.push(
                    attachDropTarget(
                        el,
                        rowId,
                        dragItem.numericId,
                        dragItem.name,
                        dragItem.isForeignShared
                    )
                );
            }
            return () => cleanups.forEach((fn) => fn());
        },
        [
            dragAndDropEnabled,
            dragItem,
            attachDraggable,
            attachDropTarget,
            rowId,
            onGeneratePreview,
        ]
    );

    return (
        <TableRow
            ref={rowRef}
            data-row-id={rowId}
            onClick={onClick}
            onKeyDown={interactive ? onKeyDown : undefined}
            tabIndex={interactive ? 0 : undefined}
            role={interactive ? "button" : undefined}
            className={cn(
                "focus-visible:ring-primary/40 focus-visible:ring-2 focus-visible:outline-none focus-visible:ring-inset",
                isBeingDragged && "opacity-40 transition-opacity duration-150",
                className
            )}
            {...rest}
        >
            {children}
        </TableRow>
    );
}
